import { buildApiUrl, getApiBaseCandidates } from "@/lib/apiBase";

export interface WorkflowStateSnapshot {
  notifications?: unknown[];
  escrows?: unknown[];
  payouts?: unknown[];
  membershipRequests?: unknown[];
  transactionRequests?: unknown[];
  rentalAgreements?: unknown[];
  chatThreads?: Record<string, unknown[]>;
  leaseAgreementCertificates?: unknown[];
  userReports?: unknown[];
  marketplaceRequestConversations?: unknown[];
  marketplaceRequests?: unknown[];
  equipmentVerificationRequests?: unknown[];
  listingDrafts?: unknown[];
  accreditationProfiles?: unknown[];
  userKycProfiles?: unknown[];
  dailyVisitGlobalState?: Record<string, unknown>;
}

let preferredWorkflowApiBase = "";
const WORKFLOW_GET_CACHE_TTL_MS = 5000;
const workflowGetRequestCache = new Map<string, { expiresAt: number; promise: Promise<Record<string, unknown>> }>();
const workflowMutationRequestCache = new Map<string, Promise<Record<string, unknown>>>();

const DUPLICATE_MUTATION_ERROR = /identical request is already being processed/i;

const readResponsePayload = async (response: Response) => {
  let payload: Record<string, unknown> = {};

  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  return payload;
};

const readResponse = <T extends Record<string, unknown>>(response: Response, payload: Record<string, unknown>) => {

  if (!response.ok) {
    throw new Error(String(payload.message || payload.error || "Workflow state request failed."));
  }

  return payload as T;
};

const workflowRequest = async <T extends Record<string, unknown>>(token: string, init?: RequestInit) => {
  const method = String(init?.method || "GET").toUpperCase();
  const isCacheableGet = method === "GET";
  const cacheKey = `${method}:${token}`;
  const mutationCacheKey = isCacheableGet ? "" : `${method}:${token}:${String(init?.body || "")}`;

  if (isCacheableGet) {
    const cached = workflowGetRequestCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.promise as Promise<T>;
    }
  } else if (workflowMutationRequestCache.has(mutationCacheKey)) {
    return workflowMutationRequestCache.get(mutationCacheKey) as Promise<T>;
  } else {
    workflowGetRequestCache.clear();
  }

  const bases = getApiBaseCandidates(preferredWorkflowApiBase);
  let lastError: Error | null = null;

  const requestPromise = (async () => {

    for (const base of bases) {
      try {
        const response = await fetch(buildApiUrl("/api/workflow-state", base), {
          ...init,
          headers: {
            ...(init?.headers || {}),
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 404 && base !== bases[bases.length - 1]) {
          lastError = new Error("Workflow state route not found.");
          continue;
        }

        const payload = await readResponsePayload(response);

        if (response.status === 409 && method === "PATCH") {
          const conflictMessage = String(payload.message || payload.error || "");

          if (DUPLICATE_MUTATION_ERROR.test(conflictMessage)) {
            preferredWorkflowApiBase = base;
            const fallback = await workflowRequest<{ success: boolean; state: WorkflowStateSnapshot | null }>(token);
            return fallback as unknown as T;
          }
        }

        const result = readResponse<T>(response, payload);
        preferredWorkflowApiBase = base;
        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error("Workflow state request failed.");
      }
    }

    throw lastError || new Error("Workflow state request failed.");
  })();

  if (isCacheableGet) {
    workflowGetRequestCache.set(cacheKey, {
      expiresAt: Date.now() + WORKFLOW_GET_CACHE_TTL_MS,
      promise: requestPromise as Promise<Record<string, unknown>>,
    });
    requestPromise.catch(() => {
      workflowGetRequestCache.delete(cacheKey);
    });
  } else {
    workflowMutationRequestCache.set(mutationCacheKey, requestPromise as Promise<Record<string, unknown>>);
    requestPromise.finally(() => {
      const current = workflowMutationRequestCache.get(mutationCacheKey);
      if (current === requestPromise) {
        workflowMutationRequestCache.delete(mutationCacheKey);
      }
    });
  }

  return requestPromise;
};

export const fetchWorkflowState = async (token: string) => workflowRequest<{ success: boolean; state: WorkflowStateSnapshot | null }>(token);

export const mergeWorkflowState = async (token: string, payload: WorkflowStateSnapshot) => workflowRequest<{ success: boolean; state: WorkflowStateSnapshot | null }>(token, {
  method: "PATCH",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify(payload),
});

export const unlinkUserAccreditationCertificateOnServer = async (token: string, userKey: string) => {
  const normalizedUser = String(userKey || "").trim().toLowerCase();
  if (!normalizedUser) {
    throw new Error("A valid user key is required.");
  }

  const bases = getApiBaseCandidates(preferredWorkflowApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/workflow-state/unlink/accreditation/${encodeURIComponent(normalizedUser)}`, base), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await readResponsePayload(response);
      const result = readResponse<{ success: boolean; state: WorkflowStateSnapshot | null }>(response, payload);
      preferredWorkflowApiBase = base;
      workflowGetRequestCache.clear();
      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Workflow state request failed.");
    }
  }

  throw lastError || new Error("Workflow state request failed.");
};

export const unlinkEquipmentVerificationIdOnServer = async (token: string, equipmentId: string) => {
  const normalizedEquipmentId = String(equipmentId || "").trim();
  if (!normalizedEquipmentId) {
    throw new Error("A valid equipment ID is required.");
  }

  const bases = getApiBaseCandidates(preferredWorkflowApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/workflow-state/unlink/equipment-verification/${encodeURIComponent(normalizedEquipmentId)}`, base), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await readResponsePayload(response);
      const result = readResponse<{ success: boolean; state: WorkflowStateSnapshot | null }>(response, payload);
      preferredWorkflowApiBase = base;
      workflowGetRequestCache.clear();
      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Workflow state request failed.");
    }
  }

  throw lastError || new Error("Workflow state request failed.");
};